import { validatePaddleConnectionInput } from '@modules/paddle-connect/utils/paddleCredentialValidation';
/**
 * @fileoverview Form for connecting Paddle account
 * @module paddle-connect/components/ConnectPaddleForm
 */

import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
  Collapse,
  IconButton,
} from '@mui/material';
import { ExpandMore, ExpandLess, HelpOutline } from '@mui/icons-material';
import { usePaddleConnect } from '../hooks/usePaddleConnect';
import { PADDLE_CONNECT_TEST_IDS } from '@modules/paddle-connect/testIds';
import type { ConnectPaddleFormProps, ConnectPaddleRequest } from '../types';

/**
 * Form for connecting Paddle account to a project
 *
 * Unlike Stripe Connect (OAuth), Paddle requires manual credential entry.
 * The form collects:
 * - API Key (server-side, stored in Secret Manager)
 * - Client Token (client-side, stored in Firestore)
 * - Webhook Secret (optional, for webhook verification)
 * - Environment (sandbox or production)
 *
 * @example
 * ```tsx
 * <ConnectPaddleForm
 *   projectId="abc123"
 *   onConnected={() => refetch()}
 *   onError={(error) => toast.error(error)}
 * />
 * ```
 */
export function ConnectPaddleForm({ projectId, onConnected, onError, defaultEnvironment }: ConnectPaddleFormProps) {
  const { connect, loading } = usePaddleConnect(projectId);

  const [apiKey, setApiKey] = useState('');
  const [clientToken, setClientToken] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [environment, setEnvironment] = useState<'sandbox' | 'production'>(defaultEnvironment || 'sandbox');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const credentials: ConnectPaddleRequest = {
      apiKey: apiKey.trim(), clientToken: clientToken.trim(), environment,
      ...(webhookSecret.trim() && { webhookSecret: webhookSecret.trim() }),
    };
    const inputError = validatePaddleConnectionInput(credentials);
    if (inputError) { setError(inputError); return; }

    try {
      await connect(credentials);
      onConnected?.();
    } catch (err) {
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      onError?.(errorMessage);
    }
  };

  return (
    <Box component="form" data-testid={PADDLE_CONNECT_TEST_IDS.form} onSubmit={handleSubmit} sx={{ maxWidth: 500 }}>
      <Typography variant="h6" gutterBottom>
        Connect Paddle Account
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter your Paddle credentials to enable payments.{' '}
        <Link
          href="https://developer.paddle.com/api-reference/about/authentication"
          target="_blank"
          rel="noopener"
        >
          Learn how to get your credentials
        </Link>
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Environment</InputLabel>
        <Select
          value={environment}
          label="Environment"
          data-testid={PADDLE_CONNECT_TEST_IDS.envToggle}
          onChange={(e) => setEnvironment(e.target.value as 'sandbox' | 'production')}
        >
          <MenuItem value="sandbox">
            Sandbox (Testing)
          </MenuItem>
          <MenuItem value="production">
            Production (Live)
          </MenuItem>
        </Select>
      </FormControl>

      <TextField
        fullWidth
        label="API Key"
        slotProps={{ htmlInput: { 'data-testid': PADDLE_CONNECT_TEST_IDS.apiKey } }}
        placeholder={environment === 'sandbox' ? 'pdl_sdbx_apikey_...' : 'pdl_live_apikey_...'}
        value={apiKey}
        onChange={(e) => setApiKey(e.target.value)}
        required
        type="password"
        autoComplete="off"
        sx={{ mb: 2 }}
        helperText="Server-side API key from Paddle Dashboard → Developer Tools → Authentication"
      />

      <TextField
        fullWidth
        label="Client Token"
        slotProps={{ htmlInput: { 'data-testid': PADDLE_CONNECT_TEST_IDS.clientToken } }}
        placeholder={environment === 'sandbox' ? 'test_...' : 'live_...'}
        value={clientToken}
        onChange={(e) => setClientToken(e.target.value)}
        required
        sx={{ mb: 2 }}
        helperText="Client-side token for Paddle.js checkout"
      />

      <Box sx={{ mb: 2 }}>
        <Button
          size="small"
          data-testid={PADDLE_CONNECT_TEST_IDS.advancedToggle}
          onClick={() => setShowAdvanced(!showAdvanced)}
          endIcon={showAdvanced ? <ExpandLess /> : <ExpandMore />}
        >
          Advanced Settings
        </Button>
      </Box>

      <Collapse in={showAdvanced}>
        <TextField
          fullWidth
          label="Webhook Secret (Optional)"
          slotProps={{ htmlInput: { 'data-testid': PADDLE_CONNECT_TEST_IDS.webhookSecret } }}
          placeholder="pdl_ntfset_..."
          value={webhookSecret}
          onChange={(e) => setWebhookSecret(e.target.value)}
          type="password"
          autoComplete="off"
          sx={{ mb: 2 }}
          helperText="Required for receiving payment events. Paddle → Developer Tools → Notifications → your destination → Secret key"
        />
      </Collapse>

      {/* Left-aligned even when a host centres the form (Paddle not-connected card, D-18 / C-20). */}
      <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }} icon={<HelpOutline />}>
        <Typography variant="body2">
          <strong>Where to find credentials:</strong>
          <br />
          1. Log in to{' '}
          <Link href="https://vendors.paddle.com" target="_blank" rel="noopener">
            Paddle Dashboard
          </Link>
          <br />
          2. Go to Developer Tools → Authentication
          <br />
          3. Create or copy your API Key and Client Token
        </Typography>
      </Alert>

      <Button
        type="submit"
        data-testid={PADDLE_CONNECT_TEST_IDS.connectSubmit}
        variant="contained"
        color="primary"
        disabled={loading || !apiKey || !clientToken}
        fullWidth
        size="large"
      >
        {loading ? 'Connecting...' : 'Connect Paddle'}
      </Button>
    </Box>
  );
}

export default ConnectPaddleForm;
